import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Divider, IconButton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ReactComponent as DownloadLogo } from 'assets/analytics_download_logo.svg'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import Input from 'components/shared/textFields/Input'
import { useExportCowsListReport } from 'hook/query/useExportLamenessReport'
import useLamenessFilter from 'hook/query/useLamenessFilter'
import { useDebounce } from 'hook/useDebounce'
import useCowsNextPage from 'hook/useNextPage'
import { useTabsController } from 'hooks/useTabsController'
import { InspectionStatus, TreatmentStatus } from 'models'
import { useCallback, useEffect, useMemo, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'
import { defaultInitialState } from 'store/lamenessFilterSlice'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import CustomPagination from 'ui/CustomPagination'
import Preloader from 'ui/Preloader'
import TooltipHover from 'ui/TooltipHover'
import TabsFilter from 'ui/breadCrumbs/TabsFilter'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { applyPageGroupIds } from '../shared/helper/applyPageGroupIds'
import { getCowsFilterStats } from '../shared/helper/getCowsFilterStats'
import LamenessTable from '../shared/LamenessTable'
import AddColumnMenu from '../shared/filterElements/AddColumnMenu'
import FilterContent from '../shared/filterElements/FilterContent'
import { useActiveColumn } from '../shared/hooks/useActiveColumn'
import LameHeadsList from './LameHeadsList'

interface ExaminationsListProps {
	isActive: boolean
	pageGroupIds?: number[]
}

const EXAMINATIONS_ORDERING = 'lameness_examination_priority'

const ExaminationsList = ({
	isActive,
	pageGroupIds,
}: ExaminationsListProps) => {
	const [page, setPage] = useState<number>(1)
	const initialPageSize = 36
	const {
		columns,
		getColumn,
		toggleColumnByTitle,
		resetColumns,
		isDefaultView,
		availableFilterOptions,
	} = useActiveColumn()
	const {
		activeFilters,
		availableFilters,
		openFilter,
		toggleFilter,
		setActiveFilters,
		setOpenFilter,
		handleDayChange,
		returnInitialProps,
		turnOnFilters,
		handleTabs,
		isFetchComplete,
		isEqualFilter,
		isEqualLamenessFilter,
		isEqualTreatmentFilter,
		isEqualInspectionFilter,
		isEqualLactationFilter,
		isEqualCowAgeFilter,
		isEqualGroupsFilter,
		isEqualFatnessFilter,
		isEqualExaminersFilter,
		isEqualDiagnosisFilter,
	} = useLamenessFilter()
	const [debouncedValue, handleChange, value] = useDebounce()

	const { data: filterSourceCows = [] } = useQuery({
		queryKey: ['examinationsFilterSource'],
		queryFn: async () => {
			const response = await CattlesApiService.getCowsList(
				undefined,
				undefined,
				1,
				10000,
				defaultInitialState,
			)
			return response.data.results
		},
		enabled: isActive,
		staleTime: 50000,
	})

	const examinationAvailableFilters = useMemo(() => {
		const stats = getCowsFilterStats(filterSourceCows)
		const withStats = <T extends { startOf: unknown; endOf: unknown }>(
			range: T,
			statsRange: { start: number | ''; end: number | '' },
		): T =>
			statsRange.start !== '' && statsRange.end !== ''
				? { ...range, startOf: statsRange.start, endOf: statsRange.end }
				: range

		return {
			...availableFilters,
			lameness: withStats(availableFilters.lameness, stats.limpnessDays),
			cowAge: withStats(availableFilters.cowAge, stats.age),
			lactation: withStats(availableFilters.lactation, stats.lactation),
			fatness: withStats(availableFilters.fatness, stats.fatness),
		}
	}, [availableFilters, filterSourceCows])

	const filtersWithPageGroups = useMemo(
		() => applyPageGroupIds(activeFilters, pageGroupIds),
		[activeFilters, pageGroupIds],
	)

	useEffect(() => {
		setPage(1)
	}, [pageGroupIds])

	const propForTable = {
		params: filtersWithPageGroups,
		isFetchComplete,
		page,
		setPage,
		initialPageSize,
		isFetchCowsListEnabled: isActive && !openFilter,
		initialTabs: initialTabs,
		tag_id_search: debouncedValue,
		ordering: EXAMINATIONS_ORDERING,
	}

	const { cowsList, totalCountPages, fetchingCowsList, availableTabs } =
		useCowsNextPage(propForTable)

	const selectedColumnsForExport = useMemo(
		() =>
			Array.from(
				new Set([
					'tag_id',
					'collar_num',
					'limpness',
					'limpness_length',
					...columns
						.filter(column => column.isShowColumn)
						.map(column => column.key),
				]),
			),
		[columns],
	)

	const propsForReport = {
		params: { ...filtersWithPageGroups, tag_id_search: debouncedValue },
		page,
		initialPageSize,
		ordering: EXAMINATIONS_ORDERING,
		columns: selectedColumnsForExport,
	}
	const { isFetchReport, setIsFetchReport } =
		useExportCowsListReport(propsForReport)

	return (
		<CardStandart
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				padding: '24px',
				width: '100%',
				maxWidth: '100%',
				minWidth: 0,
			}}
		>
			<Box
				display={'flex'}
				flexDirection={'row'}
				justifyContent={'space-between'}
				alignItems={'center'}
				mb='16px'
			>
				<Box display='flex' alignItems='center' gap='6px'>
					<TypographyHeader>Осмотры</TypographyHeader>
					<TooltipHover
						title='Таблица содержит записи о последних лечебных мероприятиях по каждой голове'
						placement='top-start'
						disableHoverBackground
					>
						<Box
							sx={{
								display: 'flex',
								color: COLORS.icon,
								cursor: 'pointer',
								'&:hover': { color: COLORS.main },
							}}
						>
							<InfoOutlinedIcon sx={{ fontSize: 18, color: 'inherit' }} />
						</Box>
					</TooltipHover>
				</Box>
				<Box
					display={'flex'}
					flexDirection={'row'}
					alignItems={'center'}
					gap={'14px'}
				>
					<Input
						width={'330px'}
						value={value}
						onChange={handleChange}
						label=''
						placeholder='Поиск по номеру бирки и ошейника'
						size='small'
						startIcon={<SearchIcon stroke={COLORS.icon} />}
						inputProps={{ style: { paddingLeft: '4px' } }}
					/>
					<AddColumnMenu
						onClick={toggleColumnByTitle}
						activeColumns={columns}
						isDefaultView={isDefaultView}
						onResetView={resetColumns}
					/>
					<FilterContent
						activeFilters={activeFilters}
						availableFilters={examinationAvailableFilters}
						openFilter={openFilter}
						toggleFilter={toggleFilter}
						setActiveFilters={setActiveFilters}
						setOpenFilter={setOpenFilter}
						handleDayChange={handleDayChange}
						returnInitialProps={returnInitialProps}
						turnOnFilters={turnOnFilters}
						titleForFilter={availableFilterOptions}
						isEqualFilter={isEqualFilter}
						isEqualLamenessFilter={isEqualLamenessFilter}
						isEqualTreatmentFilter={isEqualTreatmentFilter}
						isEqualInspectionFilter={isEqualInspectionFilter}
						isEqualLactationFilter={isEqualLactationFilter}
						isEqualCowAgeFilter={isEqualCowAgeFilter}
						isEqualGroupsFilter={isEqualGroupsFilter}
						isEqualFatnessFilter={isEqualFatnessFilter}
						isEqualExaminersFilter={isEqualExaminersFilter}
						isEqualDiagnosisFilter={isEqualDiagnosisFilter}
					/>
					<TooltipHover
						title='Экспорт таблицы'
						placement='bottom-end'
						oneLine
						disableHoverBackground
					>
						<IconButton
							onClick={() => {
								setIsFetchReport(true)
							}}
							size='small'
							sx={{
								borderRadius: '8px',
								'&:hover': {
									backgroundColor: COLORS.background,
								},
							}}
						>
							{isFetchReport ? (
								<Preloader color={COLORS.icon} size={24} />
							) : (
								<DownloadLogo stroke={COLORS.icon} />
							)}
						</IconButton>
					</TooltipHover>
				</Box>
			</Box>
			<Box
				display={'flex'}
				flexDirection={'row'}
				alignItems={'flex-end'}
				gap={'8px'}
			>
				<TabsFilter
					availableTabs={availableTabs}
					onClick={useCallback(
						key => {
							if (
								key === InspectionStatus.Expired ||
								key === InspectionStatus.Planned
							) {
								handleTabs('inspectionStatus', key)
							} else if (key === TreatmentStatus.DisposeRecommended) {
								handleTabs('treatmentStatus', key)
							} else {
								returnInitialProps('all')
							}
						},
						[handleTabs, returnInitialProps],
					)}
				/>
			</Box>
			<LamenessTable
				activeFilters={activeFilters}
				cattlesInfo={cowsList}
				fetchingCowsList={fetchingCowsList}
				getColumn={getColumn}
			/>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '16px',
				}}
			>
				<Divider />
				<CustomPagination
					onChangeFromServer={setPage}
					count={initialPageSize}
					totalCount={totalCountPages}
					isResetPage={!isEqualFilter}
				/>
			</Box>
		</CardStandart>
	)
}

enum LAMENESS_LIST_TABS {
	LAME_HEADS = 'lame_heads',
	EXAMINATIONS = 'examinations',
}

interface LamenessListProps {
	pageGroupIds?: number[]
}

const LamenessList = ({ pageGroupIds }: LamenessListProps) => {
	const { tab, changeTab } = useTabsController<LAMENESS_LIST_TABS>({
		keyName: 'lameness_list_tab',
		defaultValue: LAMENESS_LIST_TABS.LAME_HEADS,
	})

	const tableTabs = [
		{
			key: LAMENESS_LIST_TABS.LAME_HEADS,
			title: 'Список голов',
		},
		{
			key: LAMENESS_LIST_TABS.EXAMINATIONS,
			title: 'Осмотры',
		},
	]

	return (
		<Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
			<Box mb='16px'>
				<TabsFilter
					availableTabs={tableTabs}
					selectedKey={tab}
					onClick={key => changeTab(key as LAMENESS_LIST_TABS)}
				/>
			</Box>
			<Box
				sx={{
					display: tab === LAMENESS_LIST_TABS.LAME_HEADS ? 'block' : 'none',
					width: '100%',
					maxWidth: '100%',
					minWidth: 0,
				}}
			>
				<LameHeadsList
					isActive={tab === LAMENESS_LIST_TABS.LAME_HEADS}
					pageGroupIds={pageGroupIds}
				/>
			</Box>
			<Box
				sx={{
					display: tab === LAMENESS_LIST_TABS.EXAMINATIONS ? 'block' : 'none',
					width: '100%',
					maxWidth: '100%',
					minWidth: 0,
				}}
			>
				<ExaminationsList
					isActive={tab === LAMENESS_LIST_TABS.EXAMINATIONS}
					pageGroupIds={pageGroupIds}
				/>
			</Box>
		</Box>
	)
}

export default LamenessList

const initialTabs = [
	{
		key: 'all',
		title: 'Все',
		description: null,
	},
	{
		key: InspectionStatus.Planned,
		title: 'Планируется осмотр',
		description: null,
	},
	{
		key: InspectionStatus.Expired,
		title: 'Осмотр просрочен',
		description: null,
	},
	{
		key: TreatmentStatus.DisposeRecommended,
		title: 'Рекомендованы на выбытие',
		description: null,
	},
]
